// frontend/src/pages/admin/SettingsPage.tsx
import { useState, useEffect, type ChangeEvent } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
  CardFooter,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Settings,
  Globe,
  Palette,
  SlidersHorizontal,
  Link2,
  ShieldAlert,
  Save,
  Loader2,
  AlertCircle,
  CheckCircle,
  Phone,
  MapPin,
  Eye,
  FileText,
  LayoutDashboard,
} from "lucide-react";
import {
  getAllSettings,
  updateSettings,
  uploadLogo,
  uploadFavicon,
} from "@/api/settingService";
import {
  type Setting,
  type SettingUpdatePayload,
  type OfficeAddressSettingValue,
  type SettingValueType,
  type ImageUploadResponse,
} from "@/types";
import { getFullImageUrl as constructFullImageUrl } from "@/lib/utils";

// Define known setting keys
const KNOWN_SETTING_KEYS = {
  SITE_TITLE: "site_title",
  SITE_TAGLINE: "site_tagline",
  ADMIN_EMAIL: "admin_email",
  SITE_LOGO_URL: "site_logo_url",
  SITE_FAVICON_URL: "site_favicon_url",
  SEO_META_TITLE: "seo_meta_title",
  SEO_META_DESCRIPTION: "seo_meta_description",
  ROBOTS_TXT_CONTENT: "robots_txt_content",
  GA_TRACKING_ID: "ga_tracking_id",
  MAINTENANCE_MODE: "maintenance_mode",
  MAINTENANCE_MESSAGE: "maintenance_message",
  CONTACT_OFFICE_ADDRESS_JSON: "contact_office_address_json",
  CONTACT_PHONE_GENERAL: "contact_phone_general",
  CONTACT_EMAIL_SUPPORT: "contact_email_support",
  CONTACT_EMAIL_SALES: "contact_email_sales",
  PAGE_PRODUCTS_ENABLED: "page_products_enabled",
  PAGE_RESEARCH_ENABLED: "page_research_enabled",
  PAGE_ARTICLES_ENABLED: "page_articles_enabled",
  PAGE_ABOUT_ENABLED: "page_about_enabled",
  PAGE_CONTACT_ENABLED: "page_contact_enabled",
  PAGE_REGISTRATION_ENABLED: "page_registration_enabled",
  PRODUCTS_PAGE_HEADER_TITLE: "products_page_header_title",
  PRODUCTS_PAGE_HEADER_SUBTITLE: "products_page_header_subtitle",
  RESEARCH_PAGE_HEADER_TITLE: "research_page_header_title",
  RESEARCH_PAGE_HEADER_SUBTITLE: "research_page_header_subtitle",
  ARTICLES_PAGE_HEADER_TITLE: "articles_page_header_title",
  ARTICLES_PAGE_HEADER_SUBTITLE: "articles_page_header_subtitle",
  CONTACT_PAGE_HEADER_TITLE: "contact_page_header_title",
  CONTACT_PAGE_HEADER_SUBTITLE: "contact_page_header_subtitle",
};

const getDisplayValue = (
  settingsMap: Record<string, Setting>,
  key: string,
  defaultValue: string | boolean | number = "",
  valueTypeHint?:
    | "value_str"
    | "value_text"
    | "value_bool"
    | "value_int"
    | "value_float"
    | "value_json"
): string | boolean | number | Record<string, any> | null => {
  const setting = settingsMap[key];
  if (setting === undefined || setting === null) {
    if (valueTypeHint === "value_bool")
      return defaultValue === "" ? false : defaultValue;
    return defaultValue;
  }

  const actualValueType = setting.value_type;

  if (actualValueType === "boolean") return setting.value_bool ?? defaultValue;
  if (actualValueType === "string") return setting.value_str ?? defaultValue;
  if (actualValueType === "text") return setting.value_text ?? defaultValue;
  if (actualValueType === "integer") return setting.value_int ?? defaultValue;
  if (actualValueType === "float") return setting.value_float ?? defaultValue;
  if (actualValueType === "json")
    return (
      setting.value_json ??
      (typeof defaultValue === "object" ? defaultValue : {})
    );

  const typeToUse = valueTypeHint || actualValueType;
  if (typeToUse === "value_bool") return setting.value_bool ?? defaultValue;
  if (typeToUse === "value_str") return setting.value_str ?? defaultValue;
  if (typeToUse === "value_text") return setting.value_text ?? defaultValue;
  if (typeToUse === "value_int") return setting.value_int ?? defaultValue;
  if (typeToUse === "value_float") return setting.value_float ?? defaultValue;
  if (typeToUse === "value_json")
    return (
      setting.value_json ??
      (typeof defaultValue === "object" ? defaultValue : {})
    );

  if (setting.value_str !== null && setting.value_str !== undefined)
    return setting.value_str;
  if (setting.value_text !== null && setting.value_text !== undefined)
    return setting.value_text;
  if (setting.value_bool !== null && setting.value_bool !== undefined)
    return setting.value_bool;
  if (setting.value_int !== null && setting.value_int !== undefined)
    return setting.value_int;
  if (setting.value_float !== null && setting.value_float !== undefined)
    return setting.value_float;
  if (setting.value_json !== null && setting.value_json !== undefined)
    return setting.value_json;

  return defaultValue;
};

const SettingsPage = () => {
  const [initialSettingsMap, setInitialSettingsMap] = useState<
    Record<string, Setting>
  >({});
  const [currentSettingsMap, setCurrentSettingsMap] = useState<
    Record<string, Setting>
  >({});
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState<Record<string, boolean>>({});
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [faviconFile, setFaviconFile] = useState<File | null>(null);
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const [faviconPreview, setFaviconPreview] = useState<string | null>(null);

  const [initialOfficeAddress, setInitialOfficeAddress] = useState<
    Partial<OfficeAddressSettingValue>
  >({});
  const [officeAddressFormFields, setOfficeAddressFormFields] = useState<
    Partial<OfficeAddressSettingValue>
  >({});

  const backendBaseUrl =
    import.meta.env.VITE_API_BASE_URL?.replace("/api/v1", "") ||
    "http://localhost:8000";

  const getFullStaticUrl = (
    relativePath: string | null | undefined
  ): string | undefined => {
    return constructFullImageUrl(relativePath, backendBaseUrl);
  };

  useEffect(() => {
    const fetchSettings = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const fetchedSettingsArray = await getAllSettings();
        const newInitialMap: Record<string, Setting> = {};
        fetchedSettingsArray.forEach((setting) => {
          newInitialMap[setting.key] = setting;
        });
        console.log(fetchedSettingsArray);
        setInitialSettingsMap(newInitialMap);
        setCurrentSettingsMap(JSON.parse(JSON.stringify(newInitialMap)));

        const logoUrlSetting = newInitialMap[KNOWN_SETTING_KEYS.SITE_LOGO_URL];
        if (logoUrlSetting?.value_str) {
          // --- FIX: Handle potential 'undefined' return from getFullStaticUrl ---
          setLogoPreview(getFullStaticUrl(logoUrlSetting.value_str) ?? null);
        }

        const faviconUrlSetting =
          newInitialMap[KNOWN_SETTING_KEYS.SITE_FAVICON_URL];
        if (faviconUrlSetting?.value_str) {
          // --- FIX: Handle potential 'undefined' return from getFullStaticUrl ---
          setFaviconPreview(
            getFullStaticUrl(faviconUrlSetting.value_str) ?? null
          );
        }

        const officeAddrSetting =
          newInitialMap[KNOWN_SETTING_KEYS.CONTACT_OFFICE_ADDRESS_JSON];
        let initialAddr: Partial<OfficeAddressSettingValue> = {};
        if (officeAddrSetting?.value_json) {
          if (
            typeof officeAddrSetting.value_json === "object" &&
            officeAddrSetting.value_json !== null
          ) {
            // This handles cases where the value might already be an object.
            initialAddr = officeAddrSetting.value_json;
          } else if (typeof officeAddrSetting.value_json === "string") {
            // This handles the case from your log, where the value is a JSON string.
            try {
              initialAddr = JSON.parse(officeAddrSetting.value_json);
            } catch (e) {
              console.error(
                "Failed to parse office address JSON string:",
                officeAddrSetting.value_json,
                e
              );
              initialAddr = {}; // Fallback to an empty object on parsing error
            }
          }
        }
        setInitialOfficeAddress(initialAddr);
        setOfficeAddressFormFields({ ...initialAddr });
      } catch (err: any) {
        console.error("Failed to fetch settings:", err);
        setError(
          err.response?.data?.detail ||
            "Could not load site settings. Please try again."
        );
      } finally {
        setIsLoading(false);
      }
    };
    fetchSettings();
  }, []);

  const handleSettingChange = (
    key: string,
    value: string | boolean | number,
    valueTypeField:
      | "value_str"
      | "value_text"
      | "value_bool"
      | "value_int"
      | "value_float"
  ) => {
    setCurrentSettingsMap((prev) => {
      const baseSetting = initialSettingsMap[key] || prev[key];
      const typeMap: Record<string, SettingValueType> = {
        value_str: "string",
        value_text: "text",
        value_bool: "boolean",
        value_int: "integer",
        value_float: "float",
      };
      const inferredValueType = typeMap[valueTypeField];

      const updatedSetting: Setting = {
        id: baseSetting?.id || 0,
        key: key,
        description: baseSetting?.description,
        value_str: null,
        value_text: null,
        value_bool: null,
        value_int: null,
        value_float: null,
        value_json: null,
        [valueTypeField]: value,
        created_at: baseSetting?.created_at || new Date().toISOString(),
        updated_at: new Date().toISOString(),
        value_type: inferredValueType,
      };
      return { ...prev, [key]: updatedSetting };
    });
    setSuccessMessage(null);
  };

  const handleOfficeAddressFormFieldChange = (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setOfficeAddressFormFields((prev) => ({ ...prev, [name]: value }));
    setSuccessMessage(null);
  };

  const handleFileUpload = (
    event: ChangeEvent<HTMLInputElement>,
    type: "logo" | "favicon"
  ) => {
    if (event.target.files && event.target.files[0]) {
      const file = event.target.files[0];
      if (type === "logo") setLogoFile(file);
      else setFaviconFile(file);
      if (type === "logo") setLogoPreview(URL.createObjectURL(file));
      else setFaviconPreview(URL.createObjectURL(file));
      setError(null);
      setSuccessMessage(null);
    }
  };

  const handleSaveChanges = async (
    sectionKey: string,
    settingKeysInTab: string[]
  ) => {
    setIsSaving((prev) => ({ ...prev, [sectionKey]: true }));
    setError(null);
    setSuccessMessage(null);
    let operationSuccessful = true;

    let tempCurrentSettingsMap = { ...currentSettingsMap };

    if (sectionKey === "Appearance") {
      try {
        if (logoFile) {
          const uploadResponse = (await uploadLogo(
            logoFile
          )) as unknown as ImageUploadResponse;
          if (uploadResponse && uploadResponse.image_url) {
            const key = KNOWN_SETTING_KEYS.SITE_LOGO_URL;
            const baseSetting =
              initialSettingsMap[key] || tempCurrentSettingsMap[key];
            tempCurrentSettingsMap[key] = {
              ...(baseSetting || {}), // Spread base properties like id, description, created_at
              id: baseSetting?.id || 0,
              key: key,
              description: baseSetting?.description || "Site logo URL",
              value_str: uploadResponse.image_url,
              value_text: null,
              value_bool: null,
              value_int: null,
              value_float: null,
              value_json: null,
              value_type: "string",
              created_at: baseSetting?.created_at || new Date().toISOString(),
              updated_at: new Date().toISOString(),
            };
            setLogoPreview(getFullStaticUrl(uploadResponse.image_url) ?? null);
          }
          setLogoFile(null);
        }
        if (faviconFile) {
          const uploadResponse = (await uploadFavicon(
            faviconFile
          )) as unknown as ImageUploadResponse;
          if (uploadResponse && uploadResponse.image_url) {
            const key = KNOWN_SETTING_KEYS.SITE_FAVICON_URL;
            const baseSetting =
              initialSettingsMap[key] || tempCurrentSettingsMap[key];
            tempCurrentSettingsMap[key] = {
              ...(baseSetting || {}),
              id: baseSetting?.id || 0,
              key: key,
              description: baseSetting?.description || "Site favicon URL",
              value_str: uploadResponse.image_url,
              value_text: null,
              value_bool: null,
              value_int: null,
              value_float: null,
              value_json: null,
              value_type: "string",
              created_at: baseSetting?.created_at || new Date().toISOString(),
              updated_at: new Date().toISOString(),
            };
            setFaviconPreview(
              getFullStaticUrl(uploadResponse.image_url) ?? null
            );
          }
          setFaviconFile(null);
        }
        setCurrentSettingsMap(tempCurrentSettingsMap); // Update main state after uploads
      } catch (uploadError: any) {
        console.error(`Image upload error for ${sectionKey}:`, uploadError);
        setError(
          uploadError.response?.data?.detail ||
            `Failed to upload image. Settings for this section may not be fully saved.`
        );
        operationSuccessful = false;
      }
    }

    if (!operationSuccessful && sectionKey === "Appearance") {
      setIsSaving((prev) => ({ ...prev, [sectionKey]: false }));
      return;
    }

    // Use the potentially updated tempCurrentSettingsMap for diffing if it's the Appearance tab
    const sourceMapForCurrent =
      sectionKey === "Appearance" ? tempCurrentSettingsMap : currentSettingsMap;

    const settingsToUpdatePayload: SettingUpdatePayload[] = settingKeysInTab
      .map((key) => {
        const currentSetting = sourceMapForCurrent[key];
        const initialSetting = initialSettingsMap[key];

        let payload: Partial<SettingUpdatePayload> = { key };
        let itemHasChanged = false;

        if (key === KNOWN_SETTING_KEYS.CONTACT_OFFICE_ADDRESS_JSON) {
          if (
            JSON.stringify(officeAddressFormFields) !==
            JSON.stringify(initialOfficeAddress)
          ) {
            payload.value_json = { ...officeAddressFormFields };
            payload.value_type = "json";
            itemHasChanged = true;
          }
        } else {
          if (currentSetting) {
            // currentSetting exists if it was loaded or changed
            const currentActualValueType = currentSetting.value_type; // e.g., "str", "text", "bool"

            let currentValue: any = null;
            if (currentActualValueType === "string")
              currentValue = currentSetting.value_str;
            else if (currentActualValueType === "text")
              currentValue = currentSetting.value_text;
            else if (currentActualValueType === "boolean")
              currentValue = currentSetting.value_bool;
            else if (currentActualValueType === "integer")
              currentValue = currentSetting.value_int;
            else if (currentActualValueType === "float")
              currentValue = currentSetting.value_float;
            // JSON is handled above

            let initialValue: any = null;
            if (initialSetting) {
              const initialActualValueType = initialSetting.value_type;
              if (initialActualValueType === "string")
                initialValue = initialSetting.value_str;
              else if (initialActualValueType === "text")
                initialValue = initialSetting.value_text;
              else if (initialActualValueType === "boolean")
                initialValue = initialSetting.value_bool;
              else if (initialActualValueType === "integer")
                initialValue = initialSetting.value_int;
              else if (initialActualValueType === "float")
                initialValue = initialSetting.value_float;
            }

            if (
              currentValue !== initialValue ||
              (!initialSetting &&
                currentValue !== null &&
                currentValue !== undefined)
            ) {
              if (currentActualValueType === "string")
                payload.value_str = currentValue;
              else if (currentActualValueType === "text")
                payload.value_text = currentValue;
              else if (currentActualValueType === "boolean")
                payload.value_bool = currentValue;
              else if (currentActualValueType === "integer")
                payload.value_int = currentValue;
              else if (currentActualValueType === "float")
                payload.value_float = currentValue;

              if (currentActualValueType)
                payload.value_type = currentActualValueType;
              itemHasChanged = true;
            }

            const currentDescription = currentSetting.description;
            const initialDescription = initialSetting?.description;
            if (currentDescription !== initialDescription) {
              payload.description = currentDescription;
              itemHasChanged = true;
            }
          }
        }

        if (
          !initialSetting &&
          currentSetting?.description &&
          payload.description === undefined
        ) {
          payload.description = currentSetting.description;
          itemHasChanged = true;
        }

        // Ensure value_type is present if any value_* field is in the payload
        const hasValueFieldInPayload = [
          "value_str",
          "value_text",
          "value_int",
          "value_float",
          "value_bool",
          "value_json",
        ].some(
          (vf) =>
            payload.hasOwnProperty(vf) &&
            (payload as any)[vf] !== null &&
            (payload as any)[vf] !== undefined
        );

        if (itemHasChanged && hasValueFieldInPayload && !payload.value_type) {
          if (currentSetting?.value_type) {
            payload.value_type = currentSetting.value_type;
          } else {
            console.warn(
              `Payload for key ${key} has a value but value_type could not be robustly determined. Payload:`,
              payload
            );
            // Minimal inference based on field presence - this is a fallback
            if (payload.hasOwnProperty("value_str"))
              payload.value_type = "string";
            else if (payload.hasOwnProperty("value_text"))
              payload.value_type = "text";
            else if (payload.hasOwnProperty("value_bool"))
              payload.value_type = "boolean";
            else if (payload.hasOwnProperty("value_int"))
              payload.value_type = "integer";
            else if (payload.hasOwnProperty("value_float"))
              payload.value_type = "float";
            // JSON type is handled explicitly above
          }
        }

        return itemHasChanged ? (payload as SettingUpdatePayload) : null;
      })
      .filter((s) => s !== null) as SettingUpdatePayload[];

    if (settingsToUpdatePayload.length > 0) {
      try {
        const updatedSettingsArray = await updateSettings(
          settingsToUpdatePayload
        );
        const newInitialMap = { ...initialSettingsMap };
        // Use the map that was used for diffing to update the current state
        const baseMapForCurrentUpdate =
          sectionKey === "Appearance"
            ? tempCurrentSettingsMap
            : currentSettingsMap;
        const newCurrentMap = { ...baseMapForCurrentUpdate };

        updatedSettingsArray.forEach((s) => {
          newInitialMap[s.key] = s;
          newCurrentMap[s.key] = JSON.parse(JSON.stringify(s));
        });

        if (sectionKey === "Contact Info") {
          const updatedOfficeAddrSetting =
            newCurrentMap[KNOWN_SETTING_KEYS.CONTACT_OFFICE_ADDRESS_JSON];
          if (
            updatedOfficeAddrSetting?.value_json &&
            typeof updatedOfficeAddrSetting.value_json === "object"
          ) {
            const addrData =
              updatedOfficeAddrSetting.value_json as OfficeAddressSettingValue;
            setInitialOfficeAddress(addrData);
            setOfficeAddressFormFields({ ...addrData });
          }
        }
        setInitialSettingsMap(newInitialMap);
        setCurrentSettingsMap(newCurrentMap);
        setSuccessMessage(`${sectionKey} settings updated successfully!`);
      } catch (err: any) {
        console.error(
          `Failed to save ${sectionKey} settings via bulk update:`,
          err
        );
        setError(
          err.response?.data?.detail || `Failed to save ${sectionKey} settings.`
        );
        operationSuccessful = false;
      }
    } else if (operationSuccessful) {
      setSuccessMessage(`No changes to save for ${sectionKey}.`);
    }

    setTimeout(() => {
      setSuccessMessage(null);
      if (operationSuccessful) {
        setError(null);
      }
    }, 4000);
    setIsSaving((prev) => ({ ...prev, [sectionKey]: false }));
  };

  if (isLoading) {
    return (
      <div className="container mx-auto px-4 py-20 flex justify-center items-center min-h-screen">
        <Loader2 className="h-12 w-12 animate-spin text-primary" />{" "}
        <p className="ml-3 text-lg">Loading Site Settings...</p>
      </div>
    );
  }

  const pageVisibilitySettingsKeys = Object.values(KNOWN_SETTING_KEYS).filter(
    (key) => key.startsWith("page_") && key.endsWith("_enabled")
  );
  const pageHeaderSettingsKeys = Object.values(KNOWN_SETTING_KEYS).filter(
    (key) => key.includes("_page_header_")
  );

  return (
    <div className="container mx-auto px-4 py-8 md:py-12 space-y-10 text-foreground bg-background">
      <header className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 border-b border-border/60 pb-6 relative">
        <div className="absolute bottom-0 left-0 w-1/3 h-px bg-gradient-to-r from-primary/70 via-accent/50 to-transparent"></div>
        <div className="flex items-center">
          <Settings className="h-10 w-10 text-primary mr-4 flex-shrink-0" />
          <div>
            <h1 className="text-3xl md:text-4xl font-bold tracking-tight text-foreground">
              Site{" "}
              <span className="text-gradient-maroon-to-gold">Settings</span>
            </h1>
            <p className="mt-1 text-muted-foreground">
              {" "}
              Manage global configuration for the Vedas Trading platform.{" "}
            </p>
          </div>
        </div>
        <Button
          asChild
          variant="outline"
          className="border-primary text-primary hover:bg-primary/10 hover:text-primary transition-colors self-start sm:self-center w-full sm:w-auto"
        >
          <Link to="/admin/dashboard">
            <LayoutDashboard className="mr-2 h-4 w-4" /> Dashboard
          </Link>
        </Button>
      </header>

      {error && !Object.values(isSaving).some((s) => s) && (
        <div className="p-3 my-4 bg-destructive/10 text-destructive border border-destructive/30 rounded-md flex items-center">
          <AlertCircle className="h-5 w-5 mr-2" /> {error}
        </div>
      )}
      {successMessage && (
        <div className="p-3 my-4 bg-green-500/10 text-green-700 dark:text-green-400 border border-green-500/30 rounded-md flex items-center">
          <CheckCircle className="h-5 w-5 mr-2" /> {successMessage}
        </div>
      )}

      <Tabs defaultValue="general" className="w-full">
        <TabsList className="grid w-full grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-8 mb-8 bg-muted/50 dark:bg-muted/20 p-1 h-auto rounded-md border border-border/30">
          <TabsTrigger
            value="general"
            className="data-[state=active]:!bg-[#6a040f] data-[state=active]:text-[#ffffff] data-[state=active]:shadow-md text-muted-foreground hover:text-primary"
          >
            <Globe className="mr-2 h-4 w-4" />
            General
          </TabsTrigger>
          <TabsTrigger
            value="page_headers"
            className="data-[state=active]:!bg-[#6a040f] data-[state=active]:text-[#ffffff] data-[state=active]:shadow-md text-muted-foreground hover:text-primary"
          >
            <FileText className="mr-2 h-4 w-4" />
            Headers
          </TabsTrigger>
          <TabsTrigger
            value="contact"
            className="data-[state=active]:!bg-[#6a040f] data-[state=active]:text-[#ffffff] data-[state=active]:shadow-md text-muted-foreground hover:text-primary"
          >
            <Phone className="mr-2 h-4 w-4" />
            Contact
          </TabsTrigger>
          <TabsTrigger
            value="appearance"
            className="data-[state=active]:bg-[#6a040f] data-[state=active]:text-[#ffffff] data-[state=active]:shadow-md text-muted-foreground hover:text-primary"
          >
            <Palette className="mr-2 h-4 w-4" />
            Appearance
          </TabsTrigger>
          <TabsTrigger
            value="page_visibility"
            className="data-[state=active]:bg-[#6a040f] data-[state=active]:text-[#ffffff] data-[state=active]:shadow-md text-muted-foreground hover:text-primary"
          >
            <Eye className="mr-2 h-4 w-4" />
            Pages
          </TabsTrigger>
          <TabsTrigger
            value="seo"
            className="data-[state=active]:bg-[#6a040f] data-[state=active]:text-[#ffffff] data-[state=active]:shadow-md text-muted-foreground hover:text-primary"
          >
            <SlidersHorizontal className="mr-2 h-4 w-4" />
            SEO
          </TabsTrigger>
          <TabsTrigger
            value="integrations"
            className="data-[state=active]:bg-[#6a040f] data-[state=active]:text-[#ffffff] data-[state=active]:shadow-md text-muted-foreground hover:text-primary"
          >
            <Link2 className="mr-2 h-4 w-4" />
            Integrations
          </TabsTrigger>
          <TabsTrigger
            value="maintenance"
            className="data-[state=active]:bg-[#6a040f] data-[state=active]:text-[#ffffff] data-[state=active]:shadow-md text-muted-foreground hover:text-primary"
          >
            <ShieldAlert className="mr-2 h-4 w-4" />
            Maintenance
          </TabsTrigger>
        </TabsList>

        <TabsContent value="general">
          <Card className="bg-card border-border/30 shadow-lg">
            <CardHeader>
              <CardTitle>General Site Information</CardTitle>
              <CardDescription>
                Basic information about your website.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div>
                <Label htmlFor={KNOWN_SETTING_KEYS.SITE_TITLE}>
                  Site Title
                </Label>
                <Input
                  id={KNOWN_SETTING_KEYS.SITE_TITLE}
                  value={
                    getDisplayValue(
                      currentSettingsMap,
                      KNOWN_SETTING_KEYS.SITE_TITLE,
                      "Vedas Trading",
                      "value_str"
                    ) as string
                  }
                  onChange={(e) =>
                    handleSettingChange(
                      KNOWN_SETTING_KEYS.SITE_TITLE,
                      e.target.value,
                      "value_str"
                    )
                  }
                />
              </div>
              <div>
                <Label htmlFor={KNOWN_SETTING_KEYS.SITE_TAGLINE}>
                  Site Tagline
                </Label>
                <Input
                  id={KNOWN_SETTING_KEYS.SITE_TAGLINE}
                  value={
                    getDisplayValue(
                      currentSettingsMap,
                      KNOWN_SETTING_KEYS.SITE_TAGLINE,
                      "Financial Insights",
                      "value_str"
                    ) as string
                  }
                  onChange={(e) =>
                    handleSettingChange(
                      KNOWN_SETTING_KEYS.SITE_TAGLINE,
                      e.target.value,
                      "value_str"
                    )
                  }
                />
              </div>
              <div>
                <Label htmlFor={KNOWN_SETTING_KEYS.ADMIN_EMAIL}>
                  Administrative Email
                </Label>
                <Input
                  id={KNOWN_SETTING_KEYS.ADMIN_EMAIL}
                  type="email"
                  value={
                    getDisplayValue(
                      currentSettingsMap,
                      KNOWN_SETTING_KEYS.ADMIN_EMAIL,
                      "admin@example.com",
                      "value_str"
                    ) as string
                  }
                  onChange={(e) =>
                    handleSettingChange(
                      KNOWN_SETTING_KEYS.ADMIN_EMAIL,
                      e.target.value,
                      "value_str"
                    )
                  }
                />
              </div>
            </CardContent>
            <CardFooter>
              <Button
                onClick={() =>
                  handleSaveChanges("General", [
                    KNOWN_SETTING_KEYS.SITE_TITLE,
                    KNOWN_SETTING_KEYS.SITE_TAGLINE,
                    KNOWN_SETTING_KEYS.ADMIN_EMAIL,
                  ])
                }
                disabled={isSaving["General"]}
              >
                {isSaving["General"] ? (
                  <Loader2 className="animate-spin mr-2" />
                ) : (
                  <Save className="mr-2 h-4 w-4" />
                )}
                Save General
              </Button>
            </CardFooter>
          </Card>
        </TabsContent>

        <TabsContent value="page_headers">
          <Card className="bg-card border-border/30 shadow-lg">
            <CardHeader>
              <CardTitle>Page Header Content</CardTitle>
              <CardDescription>
                Customize the main title and subtitle displayed at the top of
                key pages.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-8">
              {[
                {
                  pageName: "Products",
                  titleKey: KNOWN_SETTING_KEYS.PRODUCTS_PAGE_HEADER_TITLE,
                  subtitleKey: KNOWN_SETTING_KEYS.PRODUCTS_PAGE_HEADER_SUBTITLE,
                  defaultTitle: "Our Products",
                  defaultSubtitle: "Discover our suite...",
                },
                {
                  pageName: "Research",
                  titleKey: KNOWN_SETTING_KEYS.RESEARCH_PAGE_HEADER_TITLE,
                  subtitleKey: KNOWN_SETTING_KEYS.RESEARCH_PAGE_HEADER_SUBTITLE,
                  defaultTitle: "Market Research",
                  defaultSubtitle: "In-depth insights...",
                },
                {
                  pageName: "Articles",
                  titleKey: KNOWN_SETTING_KEYS.ARTICLES_PAGE_HEADER_TITLE,
                  subtitleKey: KNOWN_SETTING_KEYS.ARTICLES_PAGE_HEADER_SUBTITLE,
                  defaultTitle: "Latest Articles",
                  defaultSubtitle: "Stay informed...",
                },
                {
                  pageName: "Contact",
                  titleKey: KNOWN_SETTING_KEYS.CONTACT_PAGE_HEADER_TITLE,
                  subtitleKey: KNOWN_SETTING_KEYS.CONTACT_PAGE_HEADER_SUBTITLE,
                  defaultTitle: "Contact Us",
                  defaultSubtitle: "Get in touch...",
                },
              ].map((page) => (
                <div
                  key={page.pageName}
                  className="p-4 border rounded-md space-y-4"
                >
                  <h3 className="text-lg font-semibold text-primary">
                    {page.pageName} Page
                  </h3>
                  <div>
                    <Label htmlFor={page.titleKey}>Header Title</Label>
                    <Input
                      id={page.titleKey}
                      value={
                        getDisplayValue(
                          currentSettingsMap,
                          page.titleKey,
                          page.defaultTitle,
                          "value_str"
                        ) as string
                      }
                      onChange={(e) =>
                        handleSettingChange(
                          page.titleKey,
                          e.target.value,
                          "value_str"
                        )
                      }
                    />
                  </div>
                  <div>
                    <Label htmlFor={page.subtitleKey}>Header Subtitle</Label>
                    <Textarea
                      id={page.subtitleKey}
                      value={
                        getDisplayValue(
                          currentSettingsMap,
                          page.subtitleKey,
                          page.defaultSubtitle,
                          "value_text"
                        ) as string
                      }
                      onChange={(e) =>
                        handleSettingChange(
                          page.subtitleKey,
                          e.target.value,
                          "value_text"
                        )
                      }
                      rows={3}
                    />
                  </div>
                </div>
              ))}
            </CardContent>
            <CardFooter>
              <Button
                onClick={() =>
                  handleSaveChanges("PageHeaders", pageHeaderSettingsKeys)
                }
                disabled={isSaving["PageHeaders"]}
              >
                {isSaving["PageHeaders"] ? (
                  <Loader2 className="animate-spin mr-2" />
                ) : (
                  <Save className="mr-2 h-4 w-4" />
                )}
                Save Page Headers
              </Button>
            </CardFooter>
          </Card>
        </TabsContent>

        <TabsContent value="contact">
          <Card className="bg-card border-border/30 shadow-lg">
            <CardHeader>
              <CardTitle>Firm Contact Information</CardTitle>
              <CardDescription>
                Contact details displayed on your site.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-2 p-4 border border-border/20 rounded-md">
                <h3 className="text-md font-semibold mb-2 flex items-center">
                  <MapPin className="h-5 w-5 mr-2 text-primary/80" /> Office
                  Address
                </h3>
                <div>
                  <Label htmlFor="office_line1">Address Line 1</Label>
                  <Input
                    id="office_line1"
                    name="line1"
                    value={officeAddressFormFields.line1 || ""}
                    onChange={handleOfficeAddressFormFieldChange}
                  />
                </div>
                <div>
                  <Label htmlFor="office_line2">Address Line 2</Label>
                  <Input
                    id="office_line2"
                    name="line2"
                    value={officeAddressFormFields.line2 || ""}
                    onChange={handleOfficeAddressFormFieldChange}
                  />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="office_city">City</Label>
                    <Input
                      id="office_city"
                      name="city"
                      value={officeAddressFormFields.city || ""}
                      onChange={handleOfficeAddressFormFieldChange}
                    />
                  </div>
                  <div>
                    <Label htmlFor="office_state">State/Province</Label>
                    <Input
                      id="office_state"
                      name="state"
                      value={officeAddressFormFields.state || ""}
                      onChange={handleOfficeAddressFormFieldChange}
                    />
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="office_zip">ZIP/Postal Code</Label>
                    <Input
                      id="office_zip"
                      name="zip"
                      value={officeAddressFormFields.zip || ""}
                      onChange={handleOfficeAddressFormFieldChange}
                    />
                  </div>
                  <div>
                    <Label htmlFor="office_country">Country</Label>
                    <Input
                      id="office_country"
                      name="country"
                      value={officeAddressFormFields.country || ""}
                      onChange={handleOfficeAddressFormFieldChange}
                    />
                  </div>
                </div>
                <div>
                  <Label htmlFor="office_maps_url">Google Maps URL</Label>
                  <Input
                    id="office_maps_url"
                    name="maps_url"
                    value={officeAddressFormFields.maps_url || ""}
                    onChange={handleOfficeAddressFormFieldChange}
                  />
                </div>
              </div>
              <div>
                <Label htmlFor={KNOWN_SETTING_KEYS.CONTACT_PHONE_GENERAL}>
                  General Phone
                </Label>
                <Input
                  id={KNOWN_SETTING_KEYS.CONTACT_PHONE_GENERAL}
                  value={
                    getDisplayValue(
                      currentSettingsMap,
                      KNOWN_SETTING_KEYS.CONTACT_PHONE_GENERAL,
                      "",
                      "value_str"
                    ) as string
                  }
                  onChange={(e) =>
                    handleSettingChange(
                      KNOWN_SETTING_KEYS.CONTACT_PHONE_GENERAL,
                      e.target.value,
                      "value_str"
                    )
                  }
                />
              </div>
              <div>
                <Label htmlFor={KNOWN_SETTING_KEYS.CONTACT_EMAIL_SUPPORT}>
                  Support Email
                </Label>
                <Input
                  id={KNOWN_SETTING_KEYS.CONTACT_EMAIL_SUPPORT}
                  type="email"
                  value={
                    getDisplayValue(
                      currentSettingsMap,
                      KNOWN_SETTING_KEYS.CONTACT_EMAIL_SUPPORT,
                      "",
                      "value_str"
                    ) as string
                  }
                  onChange={(e) =>
                    handleSettingChange(
                      KNOWN_SETTING_KEYS.CONTACT_EMAIL_SUPPORT,
                      e.target.value,
                      "value_str"
                    )
                  }
                />
              </div>
              <div>
                <Label htmlFor={KNOWN_SETTING_KEYS.CONTACT_EMAIL_SALES}>
                  Sales Email
                </Label>
                <Input
                  id={KNOWN_SETTING_KEYS.CONTACT_EMAIL_SALES}
                  type="email"
                  value={
                    getDisplayValue(
                      currentSettingsMap,
                      KNOWN_SETTING_KEYS.CONTACT_EMAIL_SALES,
                      "",
                      "value_str"
                    ) as string
                  }
                  onChange={(e) =>
                    handleSettingChange(
                      KNOWN_SETTING_KEYS.CONTACT_EMAIL_SALES,
                      e.target.value,
                      "value_str"
                    )
                  }
                />
              </div>
            </CardContent>
            <CardFooter>
              <Button
                onClick={() =>
                  handleSaveChanges("Contact Info", [
                    KNOWN_SETTING_KEYS.CONTACT_OFFICE_ADDRESS_JSON,
                    KNOWN_SETTING_KEYS.CONTACT_PHONE_GENERAL,
                    KNOWN_SETTING_KEYS.CONTACT_EMAIL_SUPPORT,
                    KNOWN_SETTING_KEYS.CONTACT_EMAIL_SALES,
                  ])
                }
                disabled={isSaving["Contact Info"]}
              >
                {isSaving["Contact Info"] ? (
                  <Loader2 className="animate-spin mr-2" />
                ) : (
                  <Save className="mr-2 h-4 w-4" />
                )}
                Save Contact
              </Button>
            </CardFooter>
          </Card>
        </TabsContent>

        <TabsContent value="appearance">
          <Card className="bg-card border-border/30 shadow-lg">
            <CardHeader>
              <CardTitle>Appearance & Branding</CardTitle>
              <CardDescription>Customize the look and feel.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div>
                <Label htmlFor="logoUpload">Site Logo</Label>
                <Input
                  id="logoUpload"
                  type="file"
                  accept="image/png, image/jpeg, image/svg+xml, image/webp"
                  onChange={(e) => handleFileUpload(e, "logo")}
                  className="mb-2"
                />
                {logoPreview && (
                  <img
                    src={logoPreview}
                    alt="Logo Preview"
                    className="h-16 max-w-xs border p-1 rounded bg-muted/30 object-contain"
                  />
                )}
                {!logoPreview &&
                  currentSettingsMap[KNOWN_SETTING_KEYS.SITE_LOGO_URL]
                    ?.value_str && (
                    <p className="text-xs text-muted-foreground">
                      Current:{" "}
                      {
                        currentSettingsMap[KNOWN_SETTING_KEYS.SITE_LOGO_URL]
                          ?.value_str
                      }
                    </p>
                  )}
              </div>
              <div>
                <Label htmlFor="faviconUpload">Site Favicon</Label>
                <Input
                  id="faviconUpload"
                  type="file"
                  accept="image/x-icon, image/png, image/svg+xml"
                  onChange={(e) => handleFileUpload(e, "favicon")}
                  className="mb-2"
                />
                {faviconPreview && (
                  <img
                    src={faviconPreview}
                    alt="Favicon Preview"
                    className="h-8 w-8 border p-1 rounded bg-muted/30 object-contain"
                  />
                )}
                {!faviconPreview &&
                  currentSettingsMap[KNOWN_SETTING_KEYS.SITE_FAVICON_URL]
                    ?.value_str && (
                    <p className="text-xs text-muted-foreground">
                      Current:{" "}
                      {
                        currentSettingsMap[KNOWN_SETTING_KEYS.SITE_FAVICON_URL]
                          ?.value_str
                      }
                    </p>
                  )}
              </div>
            </CardContent>
            <CardFooter>
              <Button
                onClick={() =>
                  handleSaveChanges("Appearance", [
                    KNOWN_SETTING_KEYS.SITE_LOGO_URL,
                    KNOWN_SETTING_KEYS.SITE_FAVICON_URL,
                  ])
                }
                disabled={isSaving["Appearance"]}
              >
                {isSaving["Appearance"] ? (
                  <Loader2 className="animate-spin mr-2" />
                ) : (
                  <Save className="mr-2 h-4 w-4" />
                )}
                Save Appearance
              </Button>
            </CardFooter>
          </Card>
        </TabsContent>

        <TabsContent value="page_visibility">
          <Card className="bg-card border-border/30 shadow-lg">
            <CardHeader>
              <CardTitle>Page Visibility</CardTitle>
              <CardDescription>
                Control which pages appear in the navigation menu and are
                accessible.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {pageVisibilitySettingsKeys.map((key) => {
                const pageInfo = {
                  [KNOWN_SETTING_KEYS.PAGE_PRODUCTS_ENABLED]: {
                    label: "Products Page",
                    description: 'Show "Products" in menu and allow access.',
                  },
                  [KNOWN_SETTING_KEYS.PAGE_RESEARCH_ENABLED]: {
                    label: "Research Page",
                    description: 'Show "Research" in menu and allow access.',
                  },
                  [KNOWN_SETTING_KEYS.PAGE_ARTICLES_ENABLED]: {
                    label: "Articles Page",
                    description: 'Show "Articles" in menu and allow access.',
                  },
                  [KNOWN_SETTING_KEYS.PAGE_ABOUT_ENABLED]: {
                    label: "About Page",
                    description: 'Show "About" in menu and allow access.',
                  },
                  [KNOWN_SETTING_KEYS.PAGE_CONTACT_ENABLED]: {
                    label: "Contact Page",
                    description: 'Show "Contact" in menu and allow access.',
                  },
                  [KNOWN_SETTING_KEYS.PAGE_REGISTRATION_ENABLED]: {
                    label: "Registration Link",
                    description: 'Show "Register" link in header for guests.',
                  },
                }[key] || {
                  label:
                    key
                      .replace("page_", "")
                      .replace("_enabled", "")
                      .split("_")
                      .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
                      .join(" ") + " Page",
                  description: "Toggle visibility for this page/feature.",
                };

                return (
                  <div
                    key={key}
                    className="flex items-center justify-between rounded-lg border p-3 shadow-sm"
                  >
                    <div className="space-y-0.5">
                      <Label
                        htmlFor={`${key}_toggle`}
                        className="text-base font-medium"
                      >
                        {pageInfo.label}
                      </Label>
                      <p className="text-sm text-muted-foreground">
                        {pageInfo.description}
                      </p>
                    </div>
                    <Switch
                      id={`${key}_toggle`}
                      checked={
                        getDisplayValue(
                          currentSettingsMap,
                          key,
                          true,
                          "value_bool"
                        ) as boolean
                      }
                      onCheckedChange={(checked) =>
                        handleSettingChange(key, checked, "value_bool")
                      }
                      disabled={isSaving["PageVisibility"]}
                      className="bg-vedasMaroon-700"
                    />
                  </div>
                );
              })}
            </CardContent>
            <CardFooter>
              <Button
                onClick={() =>
                  handleSaveChanges(
                    "PageVisibility",
                    pageVisibilitySettingsKeys
                  )
                }
                disabled={isSaving["PageVisibility"]}
              >
                {isSaving["PageVisibility"] ? (
                  <Loader2 className="animate-spin mr-2" />
                ) : (
                  <Save className="mr-2 h-4 w-4" />
                )}
                Save Page Visibility
              </Button>
            </CardFooter>
          </Card>
        </TabsContent>

        <TabsContent value="seo">
          <Card className="bg-card border-border/30 shadow-lg">
            <CardHeader>
              <CardTitle>SEO Configuration</CardTitle>
              <CardDescription>
                Optimize your site for search engines.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div>
                <Label htmlFor={KNOWN_SETTING_KEYS.SEO_META_TITLE}>
                  Default Meta Title
                </Label>
                <Input
                  id={KNOWN_SETTING_KEYS.SEO_META_TITLE}
                  value={
                    getDisplayValue(
                      currentSettingsMap,
                      KNOWN_SETTING_KEYS.SEO_META_TITLE,
                      "",
                      "value_str"
                    ) as string
                  }
                  onChange={(e) =>
                    handleSettingChange(
                      KNOWN_SETTING_KEYS.SEO_META_TITLE,
                      e.target.value,
                      "value_str"
                    )
                  }
                />
              </div>
              <div>
                <Label htmlFor={KNOWN_SETTING_KEYS.SEO_META_DESCRIPTION}>
                  Default Meta Description
                </Label>
                <Textarea
                  id={KNOWN_SETTING_KEYS.SEO_META_DESCRIPTION}
                  value={
                    getDisplayValue(
                      currentSettingsMap,
                      KNOWN_SETTING_KEYS.SEO_META_DESCRIPTION,
                      "",
                      "value_text"
                    ) as string
                  }
                  onChange={(e) =>
                    handleSettingChange(
                      KNOWN_SETTING_KEYS.SEO_META_DESCRIPTION,
                      e.target.value,
                      "value_text"
                    )
                  }
                  rows={3}
                  className="min-h-[80px]"
                />
              </div>
              <div>
                <Label htmlFor={KNOWN_SETTING_KEYS.ROBOTS_TXT_CONTENT}>
                  Robots.txt Content
                </Label>
                <Textarea
                  id={KNOWN_SETTING_KEYS.ROBOTS_TXT_CONTENT}
                  value={
                    getDisplayValue(
                      currentSettingsMap,
                      KNOWN_SETTING_KEYS.ROBOTS_TXT_CONTENT,
                      "User-agent: *\nAllow: /",
                      "value_text"
                    ) as string
                  }
                  onChange={(e) =>
                    handleSettingChange(
                      KNOWN_SETTING_KEYS.ROBOTS_TXT_CONTENT,
                      e.target.value,
                      "value_text"
                    )
                  }
                  rows={5}
                  className="font-mono text-sm min-h-[100px]"
                />
              </div>
            </CardContent>
            <CardFooter>
              <Button
                onClick={() =>
                  handleSaveChanges("SEO", [
                    KNOWN_SETTING_KEYS.SEO_META_TITLE,
                    KNOWN_SETTING_KEYS.SEO_META_DESCRIPTION,
                    KNOWN_SETTING_KEYS.ROBOTS_TXT_CONTENT,
                  ])
                }
                disabled={isSaving["SEO"]}
              >
                {isSaving["SEO"] ? (
                  <Loader2 className="animate-spin mr-2" />
                ) : (
                  <Save className="mr-2 h-4 w-4" />
                )}
                Save SEO
              </Button>
            </CardFooter>
          </Card>
        </TabsContent>

        <TabsContent value="integrations">
          <Card className="bg-card border-border/30 shadow-lg">
            <CardHeader>
              <CardTitle>Third-Party Integrations</CardTitle>
              <CardDescription>
                Connect with external services and APIs.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div>
                <Label htmlFor={KNOWN_SETTING_KEYS.GA_TRACKING_ID}>
                  Google Analytics ID
                </Label>
                <Input
                  id={KNOWN_SETTING_KEYS.GA_TRACKING_ID}
                  value={
                    getDisplayValue(
                      currentSettingsMap,
                      KNOWN_SETTING_KEYS.GA_TRACKING_ID,
                      "",
                      "value_str"
                    ) as string
                  }
                  onChange={(e) =>
                    handleSettingChange(
                      KNOWN_SETTING_KEYS.GA_TRACKING_ID,
                      e.target.value,
                      "value_str"
                    )
                  }
                  placeholder="G-XXXXXXXXXX or UA-XXXXX-Y"
                />
              </div>
            </CardContent>
            <CardFooter>
              <Button
                onClick={() =>
                  handleSaveChanges("Integrations", [
                    KNOWN_SETTING_KEYS.GA_TRACKING_ID,
                  ])
                }
                disabled={isSaving["Integrations"]}
              >
                {isSaving["Integrations"] ? (
                  <Loader2 className="animate-spin mr-2" />
                ) : (
                  <Save className="mr-2 h-4 w-4" />
                )}
                Save Integrations
              </Button>
            </CardFooter>
          </Card>
        </TabsContent>

        <TabsContent value="maintenance">
          <Card className="bg-card border-border/30 shadow-lg">
            <CardHeader>
              <CardTitle>Maintenance Mode</CardTitle>
              <CardDescription>
                Temporarily take your site offline for updates.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="flex items-center space-x-3 rounded-md border border-border/50 p-4 !bg-input">
                <Switch
                  id="maintenance-mode-toggle"
                  checked={
                    getDisplayValue(
                      currentSettingsMap,
                      KNOWN_SETTING_KEYS.MAINTENANCE_MODE,
                      false,
                      "value_bool"
                    ) as boolean
                  }
                  onCheckedChange={(checked) =>
                    handleSettingChange(
                      KNOWN_SETTING_KEYS.MAINTENANCE_MODE,
                      checked,
                      "value_bool"
                    )
                  }
                  className="bg-vedasMaroon-700"
                />
                <Label
                  htmlFor="maintenance-mode-toggle"
                  className="text-base cursor-pointer"
                >
                  Enable Maintenance Mode
                </Label>
              </div>
              {getDisplayValue(
                currentSettingsMap,
                KNOWN_SETTING_KEYS.MAINTENANCE_MODE,
                false,
                "value_bool"
              ) && (
                <div className="space-y-2">
                  <Label htmlFor={KNOWN_SETTING_KEYS.MAINTENANCE_MESSAGE}>
                    Custom Maintenance Message
                  </Label>
                  <Textarea
                    id={KNOWN_SETTING_KEYS.MAINTENANCE_MESSAGE}
                    value={
                      getDisplayValue(
                        currentSettingsMap,
                        KNOWN_SETTING_KEYS.MAINTENANCE_MESSAGE,
                        "Our site is currently undergoing scheduled maintenance. We should be back shortly. Thank you for your patience.",
                        "value_text"
                      ) as string
                    }
                    onChange={(e) =>
                      handleSettingChange(
                        KNOWN_SETTING_KEYS.MAINTENANCE_MESSAGE,
                        e.target.value,
                        "value_text"
                      )
                    }
                    rows={4}
                    className="min-h-[100px]"
                  />
                </div>
              )}
            </CardContent>
            <CardFooter>
              <Button
                onClick={() =>
                  handleSaveChanges("Maintenance", [
                    KNOWN_SETTING_KEYS.MAINTENANCE_MODE,
                    KNOWN_SETTING_KEYS.MAINTENANCE_MESSAGE,
                  ])
                }
                disabled={isSaving["Maintenance"]}
              >
                {isSaving["Maintenance"] ? (
                  <Loader2 className="animate-spin mr-2" />
                ) : (
                  <Save className="mr-2 h-4 w-4" />
                )}
                Save Maintenance
              </Button>
            </CardFooter>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default SettingsPage;
